import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Loader2, Tag, Pencil, Trash2, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/lib/error';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { SlideOver } from '@/components/ui/SlideOver';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import { useAuth } from '@/hooks/useAuth';
import {
  useRetailLookups,
  useCreateLookup,
  useUpdateLookup,
  useDeleteLookup,
} from '@/hooks/api/useRetailLookups';
import {
  useAllergens,
  useCreateAllergen,
  useUpdateAllergen,
  useDeleteAllergen,
  useMenuSections,
  useCreateMenuSection,
  useUpdateMenuSection,
  useDeleteMenuSection,
} from '@/hooks/api/useRetailAttributes';
import {
  RetailLookupType,
  UserRole,
  type RetailAllergen,
  type RetailLookup,
  type RetailMenuSection,
} from '@pms/shared';

// ─── Tab config ───────────────────────────────────────────────────────────────

type LookupTabConfig = { kind: 'lookup'; key: RetailLookupType; label: string; singular: string; param: string };
type AttrTabConfig = { kind: 'allergen' | 'menu-section'; label: string; singular: string; param: string };
type TabConfig = LookupTabConfig | AttrTabConfig;

const TABS: TabConfig[] = [
  { kind: 'lookup', key: RetailLookupType.DEPARTMENT, label: 'Departments', singular: 'Department', param: 'department' },
  { kind: 'lookup', key: RetailLookupType.CATEGORY, label: 'Categories', singular: 'Category', param: 'category' },
  { kind: 'lookup', key: RetailLookupType.BRAND, label: 'Brands', singular: 'Brand', param: 'brand' },
  { kind: 'lookup', key: RetailLookupType.TAG, label: 'Tags', singular: 'Tag', param: 'tag' },
  { kind: 'allergen', label: 'Allergens', singular: 'Allergen', param: 'allergen' },
  { kind: 'menu-section', label: 'Menu Sections', singular: 'Menu Section', param: 'menu-section' },
];

// ─── Page ────────────────────────────────────────────────────────────────────

export function LookupsPage() {
  const propertyId = useSelectedPropertyId();
  const { user } = useAuth();
  const canManage =
    user?.role === UserRole.ADMIN || user?.role === UserRole.SUPER_ADMIN;

  const [searchParams, setSearchParams] = useSearchParams();
  const activeParam = searchParams.get('tab');
  const activeTab = TABS.find((t) => t.param === activeParam) ?? TABS[0];

  if (!canManage) {
    return (
      <div>
        <PageHeader title="Lookups" description="Departments, categories, brands and tags" />
        <EmptyState
          icon={Lock}
          title="No access"
          description="You don't have access to lookups."
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Lookups" description="Departments, categories, brands and tags" />

      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex gap-6" aria-label="Lookup types">
          {TABS.map((t) => (
            <button
              key={t.param}
              type="button"
              onClick={() => setSearchParams({ tab: t.param })}
              className={cn(
                'whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium',
                t.param === activeTab.param
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
              )}
              aria-current={t.param === activeTab.param ? 'page' : undefined}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab.kind === 'lookup' ? (
        <LookupTab
          key={activeTab.param}
          type={activeTab.key}
          label={activeTab.label}
          singular={activeTab.singular}
          propertyId={propertyId}
        />
      ) : activeTab.kind === 'allergen' ? (
        <AllergenTab key={activeTab.param} propertyId={propertyId} />
      ) : (
        <MenuSectionTab key={activeTab.param} propertyId={propertyId} />
      )}
    </div>
  );
}

// ─── Tab ──────────────────────────────────────────────────────────────────────

function LookupTab({
  type,
  label,
  singular,
  propertyId,
}: {
  type: RetailLookupType;
  label: string;
  singular: string;
  propertyId: string;
}) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<RetailLookup | null>(null);
  const [search, setSearch] = useState('');

  const listQuery = useRetailLookups({ type, propertyId });
  const deleteMutation = useDeleteLookup(type);

  const allItems: RetailLookup[] = listQuery.data?.data ?? [];
  const items: RetailLookup[] = useMemo(() => {
    if (!search.trim()) return allItems;
    const q = search.toLowerCase();
    return allItems.filter((l) => l.name.toLowerCase().includes(q));
  }, [allItems, search]);

  function handleDelete(item: RetailLookup) {
    if (!window.confirm(`Delete this ${singular.toLowerCase()}?`)) return;
    deleteMutation.mutate(item.id, {
      onError: (err) => {
        // Server soft-blocks when referenced by products (409 IN_USE).
        window.alert(extractErrorMessage(err));
      },
    });
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-3">
        <input
          type="text"
          placeholder={`Search ${label.toLowerCase()}...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-64"
          aria-label={`Search ${label.toLowerCase()}`}
        />
        <Button onClick={() => { setEditing(null); setShowForm(true); }} disabled={!propertyId}>
          <Plus size={16} aria-hidden="true" />
          Add {singular}
        </Button>
      </div>

      {!propertyId ? (
        <EmptyState icon={Tag} title="Select a property" description="Select a property to continue." />
      ) : listQuery.isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-primary-500" aria-label="Loading" />
        </div>
      ) : listQuery.isError ? (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(listQuery.error)}
        </p>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Tag}
          title={`No ${label.toLowerCase()}`}
          description={`Create your first ${singular.toLowerCase()}.`}
          actionLabel={`Add ${singular}`}
          onAction={() => { setEditing(null); setShowForm(true); }}
        />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Active</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((l) => (
                  <tr key={l.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{l.name}</td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                          l.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500',
                        )}
                      >
                        {l.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => { setEditing(l); setShowForm(true); }} className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50" aria-label="Edit">
                          <Pencil size={14} />
                        </button>
                        <button type="button" onClick={() => handleDelete(l)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" aria-label="Delete">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <SlideOver
        isOpen={showForm}
        onClose={() => { setShowForm(false); setEditing(null); }}
        title={editing ? `Edit ${singular}` : `Add ${singular}`}
        width="max-w-xl"
      >
        <LookupForm
          type={type}
          propertyId={propertyId}
          editing={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
        />
      </SlideOver>
    </div>
  );
}

// ─── Form ────────────────────────────────────────────────────────────────────

function LookupForm({
  type,
  propertyId,
  editing,
  onClose,
}: {
  type: RetailLookupType;
  propertyId: string;
  editing: RetailLookup | null;
  onClose: () => void;
}) {
  const [name, setName] = useState(editing?.name ?? '');
  const [isActive, setIsActive] = useState(editing?.isActive ?? true);

  const createMutation = useCreateLookup(type);
  const updateMutation = useUpdateLookup(type);
  const mutationError = createMutation.error ?? updateMutation.error;

  const isValid = name.trim().length > 0;

  function clearError() {
    if (createMutation.isError) createMutation.reset();
    if (updateMutation.isError) updateMutation.reset();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid) return;

    if (editing) {
      updateMutation.mutate(
        {
          id: editing.id,
          payload: { name: name.trim(), isActive },
        },
        { onSuccess: () => onClose() },
      );
    } else {
      createMutation.mutate(
        { type, propertyId, name: name.trim(), isActive },
        { onSuccess: () => onClose() },
      );
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="lk-name" className="block text-sm font-medium text-gray-700 mb-1">
          Name <span className="text-red-500">*</span>
        </label>
        <input id="lk-name" type="text" required value={name} onChange={(e) => { setName(e.target.value); clearError(); }} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>
      <div className="flex items-center gap-2">
        <input id="lk-active" type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
        <label htmlFor="lk-active" className="text-sm font-medium text-gray-700">Active</label>
      </div>

      {mutationError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(mutationError)}
        </p>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
        <Button type="submit" isLoading={createMutation.isPending || updateMutation.isPending} disabled={!isValid}>
          {editing ? 'Update' : 'Create'}
        </Button>
      </div>
    </form>
  );
}

// ─── Allergen tab ───────────────────────────────────────────────────────────

function AllergenTab({ propertyId }: { propertyId: string }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<RetailAllergen | null>(null);
  const [search, setSearch] = useState('');

  const listQuery = useAllergens({ propertyId }, !!propertyId);
  const deleteMutation = useDeleteAllergen();

  const allItems: RetailAllergen[] = listQuery.data?.data ?? [];
  const items = useMemo(() => {
    if (!search.trim()) return allItems;
    const q = search.toLowerCase();
    return allItems.filter((a) => a.name.toLowerCase().includes(q));
  }, [allItems, search]);

  function handleDelete(item: RetailAllergen) {
    if (!window.confirm('Delete this allergen?')) return;
    deleteMutation.mutate(item.id, {
      onError: (err) => window.alert(extractErrorMessage(err)),
    });
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-3">
        <input
          type="text"
          placeholder="Search allergens..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-64"
          aria-label="Search allergens"
        />
        <Button onClick={() => { setEditing(null); setShowForm(true); }} disabled={!propertyId}>
          <Plus size={16} aria-hidden="true" />
          Add Allergen
        </Button>
      </div>

      {!propertyId ? (
        <EmptyState icon={Tag} title="Select a property" description="Select a property to continue." />
      ) : listQuery.isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-primary-500" aria-label="Loading" />
        </div>
      ) : listQuery.isError ? (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(listQuery.error)}
        </p>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Tag}
          title="No allergens"
          description="Create your first allergen."
          actionLabel="Add Allergen"
          onAction={() => { setEditing(null); setShowForm(true); }}
        />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Active</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((a) => (
                  <tr key={a.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{a.name}</td>
                    <td className="px-4 py-3">
                      <span className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium', a.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500')}>
                        {a.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => { setEditing(a); setShowForm(true); }} className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50" aria-label="Edit">
                          <Pencil size={14} />
                        </button>
                        <button type="button" onClick={() => handleDelete(a)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" aria-label="Delete">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <SlideOver
        isOpen={showForm}
        onClose={() => { setShowForm(false); setEditing(null); }}
        title={editing ? 'Edit Allergen' : 'Add Allergen'}
        width="max-w-xl"
      >
        <AllergenForm propertyId={propertyId} editing={editing} onClose={() => { setShowForm(false); setEditing(null); }} />
      </SlideOver>
    </div>
  );
}

function AllergenForm({
  propertyId,
  editing,
  onClose,
}: {
  propertyId: string;
  editing: RetailAllergen | null;
  onClose: () => void;
}) {
  const [name, setName] = useState(editing?.name ?? '');
  const [isActive, setIsActive] = useState(editing?.isActive ?? true);

  const createMutation = useCreateAllergen();
  const updateMutation = useUpdateAllergen();
  const mutationError = createMutation.error ?? updateMutation.error;
  const isValid = name.trim().length > 0;

  function clearError() {
    if (createMutation.isError) createMutation.reset();
    if (updateMutation.isError) updateMutation.reset();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid) return;
    if (editing) {
      updateMutation.mutate(
        { id: editing.id, payload: { name: name.trim(), isActive } },
        { onSuccess: () => onClose() },
      );
    } else {
      createMutation.mutate(
        { propertyId, name: name.trim(), isActive },
        { onSuccess: () => onClose() },
      );
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="al-name" className="block text-sm font-medium text-gray-700 mb-1">
          Name <span className="text-red-500">*</span>
        </label>
        <input id="al-name" type="text" required value={name} onChange={(e) => { setName(e.target.value); clearError(); }} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>
      <div className="flex items-center gap-2">
        <input id="al-active" type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
        <label htmlFor="al-active" className="text-sm font-medium text-gray-700">Active</label>
      </div>

      {mutationError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(mutationError)}
        </p>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
        <Button type="submit" isLoading={createMutation.isPending || updateMutation.isPending} disabled={!isValid}>
          {editing ? 'Update' : 'Create'}
        </Button>
      </div>
    </form>
  );
}

// ─── Menu section tab ─────────────────────────────────────────────────────────

function MenuSectionTab({ propertyId }: { propertyId: string }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<RetailMenuSection | null>(null);
  const [search, setSearch] = useState('');

  const listQuery = useMenuSections({ propertyId }, !!propertyId);
  const deleteMutation = useDeleteMenuSection();

  const allItems: RetailMenuSection[] = listQuery.data?.data ?? [];
  const items = useMemo(() => {
    if (!search.trim()) return allItems;
    const q = search.toLowerCase();
    return allItems.filter((m) => m.name.toLowerCase().includes(q));
  }, [allItems, search]);

  function handleDelete(item: RetailMenuSection) {
    if (!window.confirm('Delete this menu section?')) return;
    deleteMutation.mutate(item.id, {
      onError: (err) => window.alert(extractErrorMessage(err)),
    });
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-3">
        <input
          type="text"
          placeholder="Search menu sections..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-64"
          aria-label="Search menu sections"
        />
        <Button onClick={() => { setEditing(null); setShowForm(true); }} disabled={!propertyId}>
          <Plus size={16} aria-hidden="true" />
          Add Menu Section
        </Button>
      </div>

      {!propertyId ? (
        <EmptyState icon={Tag} title="Select a property" description="Select a property to continue." />
      ) : listQuery.isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-primary-500" aria-label="Loading" />
        </div>
      ) : listQuery.isError ? (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(listQuery.error)}
        </p>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Tag}
          title="No menu sections"
          description="Create your first menu section."
          actionLabel="Add Menu Section"
          onAction={() => { setEditing(null); setShowForm(true); }}
        />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Order</Th>
                  <Th>Active</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((m) => (
                  <tr key={m.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{m.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{m.sortOrder}</td>
                    <td className="px-4 py-3">
                      <span className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium', m.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500')}>
                        {m.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => { setEditing(m); setShowForm(true); }} className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50" aria-label="Edit">
                          <Pencil size={14} />
                        </button>
                        <button type="button" onClick={() => handleDelete(m)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" aria-label="Delete">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <SlideOver
        isOpen={showForm}
        onClose={() => { setShowForm(false); setEditing(null); }}
        title={editing ? 'Edit Menu Section' : 'Add Menu Section'}
        width="max-w-xl"
      >
        <MenuSectionForm propertyId={propertyId} editing={editing} onClose={() => { setShowForm(false); setEditing(null); }} />
      </SlideOver>
    </div>
  );
}

function MenuSectionForm({
  propertyId,
  editing,
  onClose,
}: {
  propertyId: string;
  editing: RetailMenuSection | null;
  onClose: () => void;
}) {
  const [name, setName] = useState(editing?.name ?? '');
  const [sortOrder, setSortOrder] = useState(
    editing?.sortOrder != null ? String(editing.sortOrder) : '0',
  );
  const [isActive, setIsActive] = useState(editing?.isActive ?? true);

  const createMutation = useCreateMenuSection();
  const updateMutation = useUpdateMenuSection();
  const mutationError = createMutation.error ?? updateMutation.error;
  const isValid = name.trim().length > 0;

  function clearError() {
    if (createMutation.isError) createMutation.reset();
    if (updateMutation.isError) updateMutation.reset();
  }

  function parsedSortOrder(): number {
    const n = parseInt(sortOrder, 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid) return;
    if (editing) {
      updateMutation.mutate(
        { id: editing.id, payload: { name: name.trim(), sortOrder: parsedSortOrder(), isActive } },
        { onSuccess: () => onClose() },
      );
    } else {
      createMutation.mutate(
        { propertyId, name: name.trim(), sortOrder: parsedSortOrder(), isActive },
        { onSuccess: () => onClose() },
      );
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="ms-name" className="block text-sm font-medium text-gray-700 mb-1">
          Name <span className="text-red-500">*</span>
        </label>
        <input id="ms-name" type="text" required value={name} onChange={(e) => { setName(e.target.value); clearError(); }} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>
      <div>
        <label htmlFor="ms-order" className="block text-sm font-medium text-gray-700 mb-1">Sort order</label>
        <input id="ms-order" type="number" min="0" step="1" value={sortOrder} onChange={(e) => { setSortOrder(e.target.value); clearError(); }} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
        <p className="mt-1 text-xs text-gray-400">Lower numbers sort first on the terminal grid.</p>
      </div>
      <div className="flex items-center gap-2">
        <input id="ms-active" type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
        <label htmlFor="ms-active" className="text-sm font-medium text-gray-700">Active</label>
      </div>

      {mutationError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(mutationError)}
        </p>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
        <Button type="submit" isLoading={createMutation.isPending || updateMutation.isPending} disabled={!isValid}>
          {editing ? 'Update' : 'Create'}
        </Button>
      </div>
    </form>
  );
}

// ─── Shared ──────────────────────────────────────────────────────────────────

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
      {children}
    </th>
  );
}
